/* eslint-disable no-underscore-dangle */
import { useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { appLanguageSelector } from '../../../redux/app/selectors'
import type { DimensionQueryParams, FinacialStatementType, FinancialStatementsFilters } from '../types'
import { EnhancedStatementRow } from '../utils/dataEnhancement'
import { toFilterKeys } from '../utils/filterConverters'
import { useFinancialStatementsQuery } from '../queries/useFinancialStatementsQuery'
import { useStatementTableColumns } from './useStatementTableColumns'
import { contexFiltersSelector } from '../../../redux/context/filters/selectors'
import { useCtxDimensions } from '../../dimension/queries/useCtxDimensions'
import { dimensionChildrenMapSelector } from '../../dimension/selectors/selectors'

/**
 * Custom hook for managing Financial Statements component state
 * Centralizes state logic and provides clean interface for components
 *
 * @param isBudgetingPage - Whether we're on budgeting page (vs reporting page)
 *                          Controls whether to apply budget-specific enhancements
 */
export const useFinancialStatementsState = (isBudgetingPage = false) => {
  // Redux state (gradually migrate away from this)
  const companyId = useSelector(contextCompanyIdSelector)
  const filters = useSelector(contexFiltersSelector)
  const lng = useSelector(appLanguageSelector)
  const columns = useStatementTableColumns()
  const { data: dimensionMap } = useCtxDimensions(dimensionChildrenMapSelector)
  const { data: dimensionList = [] } = useCtxDimensions()

  // Local component state
  const [currentTab, setCurrentTab] = useState<FinacialStatementType>('incomeStatement')

  // Memoized filter objects for React Query
  const financialStatementsFilters: FinancialStatementsFilters = useMemo(
    () => ({
      companyId,
      budgetingScenarioId: filters.budgetingScenario?.id,
      periodGroups: filters.periodGroups,
      dimensions: filters.dimensionsQuery,
      dimension: filters.dimensions?.[0] ?? null,
      lng
    }),
    [companyId, filters.budgetingScenario, filters.periodGroups, filters.dimensionsQuery, lng]
  )
  const filterKeys = useMemo(() => toFilterKeys(financialStatementsFilters), [financialStatementsFilters])
  // React Query for financial statements data
  const {
    data: statementData,
    isLoading: statementLoading,
    isFetching: statementFetching,
    error: statementError,
    refetch: statementRefetch
  } = useFinancialStatementsQuery(filterKeys, dimensionMap, isBudgetingPage)

  const getDimensionParams = useCallback(
    (row: EnhancedStatementRow) => {
      const dimensionId = row.dimensionId ? row.dimensionId : filters.dimensions?.[0] ?? null
      const dimensionIds = dimensionMap?.get(dimensionId)?.map(d => d.dimensionId) ?? []

      const dimensionParams: DimensionQueryParams = {
        dimensionIds,
        path: row.path,
        parentId: row.dimensionId || null,
        accountCode: row._sourceAccountCode,
        statementRowId: row._sourceStatementRowId,
        companyId: financialStatementsFilters.companyId!,
        budgetingScenarioId: financialStatementsFilters.budgetingScenarioId,
        periodGroups: financialStatementsFilters.periodGroups
      }

      return dimensionParams
    },
    [financialStatementsFilters, filters.dimensions, dimensionMap]
  )

  // Action handlers
  const handleTabChange = useCallback((key: string) => {
    setCurrentTab(key as FinacialStatementType)
  }, [])

  return {
    // Core state
    companyId,
    currentTab,
    columns,
    dimensionMap,
    dimensionList,

    // Filter objects for queries
    financialStatementsFilters,
    filterKeys,

    // Query data
    statementData,
    statementLoading,
    statementFetching,
    statementError,
    statementRefetch,

    // Processed tree data
    // treeData,

    getDimensionParams,

    // Actions
    handleTabChange,

    // Additional data
    displayEmptyRows: filters.displayEmptyRows
  }
}
